import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhActiveResponseApi } from 'src/api/mindtime/wazuhActiveResponseApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { useWazuhScope, type WazuhDateRangeOption } from 'src/mindtime/hooks/useWazuhScope';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import type {
  WazuhActiveResponseEndpointItem,
  WazuhActiveResponseItem,
  WazuhActiveResponseQuery,
  WazuhActiveResponseSummaryData,
} from 'src/types/mindtime/wazuhActiveResponse';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';
import { mapDateRangeToRange } from './automatedResponseDetailModel';

export type AutomatedResponseFilters = {
  status?: string;
  actionLabel?: string;
  severityBand?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type AutomatedResponseState = {
  loading: boolean;
  summary: WazuhActiveResponseSummaryData | null;
  items: WazuhPagedData<WazuhActiveResponseItem> | null;
  endpoints: WazuhPagedData<WazuhActiveResponseEndpointItem> | null;
};

type BuildActiveResponseQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  dateRange?: WazuhDateRangeOption;
  search?: string;
  filters?: AutomatedResponseFilters;
  page?: number;
  pageSize?: number;
};

const emptyState = (): AutomatedResponseState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
});

export const buildActiveResponseQuery = ({
  scope,
  profileOrgId,
  isReseller,
  dateRange,
  search = '',
  filters = {},
  selectedEndpointId,
  page,
  pageSize,
}: BuildActiveResponseQueryInput & { selectedEndpointId?: string | null }): WazuhActiveResponseQuery => {
  const { status, actionLabel, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });
  const range = mapDateRangeToRange(dateRange);

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(actionLabel ? { actionLabel } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(range ? { range } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useAutomatedResponseExport = (
  search = '',
  filters: AutomatedResponseFilters = {}
) => {
  const { scope, isReseller, dateRange, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('automated-response');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhActiveResponseApi
      .export(
        buildActiveResponseQuery({
          scope,
          profileOrgId,
          isReseller,
          dateRange,
          search,
          filters,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'active_responses_export.csv';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useAutomatedResponseExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, dateRange, search, filters, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useAutomatedResponseData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: AutomatedResponseFilters = {}
): AutomatedResponseState => {
  const { scope, isReseller, dateRange, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('automated-response');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'automated-response');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<AutomatedResponseState>(emptyState);

  const { status, actionLabel, severityBand, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as AutomatedResponseState);
      return undefined;
    }

    if (!canSummary) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;

    const summaryParams = buildActiveResponseQuery({
      scope,
      profileOrgId,
      isReseller,
      dateRange,
      filters: { organizationId },
    });

    mindtimeWazuhActiveResponseApi
      .getSummary(summaryParams)
      .then((summary) => {
        if (!cancelled) setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useAutomatedResponseData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    dateRange,
    organizationId,
    isPreviewMode,
    wait,
    getPreviewState,
    canSummary,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canList) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const listParams = buildActiveResponseQuery({
      scope,
      profileOrgId,
      isReseller,
      dateRange,
      search,
      filters,
      page,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhActiveResponseApi
      .getResponses(listParams)
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, loading: false, items }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useAutomatedResponseData.list');
          setState((prev) => ({ ...prev, loading: false, items: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    dateRange,
    page,
    pageSize,
    search,
    status,
    actionLabel,
    severityBand,
    wazuhAgentId,
    organizationId,
    skipLive,
    canList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canAffectedEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;

    const listParams = buildActiveResponseQuery({
      scope,
      profileOrgId,
      isReseller,
      dateRange,
      search,
      filters,
      page: 0,
      pageSize,
      selectedEndpointId,
    });

    mindtimeWazuhActiveResponseApi
      .listEndpoints(listParams)
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useAutomatedResponseData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    dateRange,
    pageSize,
    search,
    status,
    actionLabel,
    severityBand,
    wazuhAgentId,
    organizationId,
    skipLive,
    canAffectedEndpoints,
  ]);

  return state;
};
